import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BusinessAnalyticsRow } from '../api/useBusinessAnalytics';

type Primitive = string | number | boolean | null | undefined;

export interface Column {
  key: keyof BusinessAnalyticsRow;
  label: string;
  group: string;
  format?: (v: Primitive) => string;
  align?: 'left' | 'right';
}

function fmtInt(v: Primitive): string {
  if (v === null || v === undefined) return '—';
  return Number(v).toLocaleString();
}

function fmtNum(v: Primitive): string {
  if (v === null || v === undefined) return '—';
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtDate(v: Primitive): string {
  if (v === null || v === undefined || v === '') return '—';
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toISOString().slice(0, 10);
}

function fmtStr(v: Primitive): string {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

export const COLUMNS: Column[] = [
  { key: 'business_name', label: 'Business', group: 'Meta', format: fmtStr, align: 'left' },

  { key: 'check_ins_total', label: 'Total Check-ins', group: 'Check-ins', format: fmtInt, align: 'right' },
  { key: 'check_ins_distinct_users', label: 'Check-in Users', group: 'Check-ins', format: fmtInt, align: 'right' },
  { key: 'going_intents_submitted', label: 'Going Intents', group: 'Check-ins', format: fmtInt, align: 'right' },
  { key: 'last_check_in_at', label: 'Last Check-in', group: 'Check-ins', format: fmtDate, align: 'left' },

  { key: 'live_check_ins_started', label: 'Live Sessions Started', group: 'Live Check-ins', format: fmtInt, align: 'right' },
  { key: 'live_check_in_distinct_users', label: 'Unique Live Users', group: 'Live Check-ins', format: fmtInt, align: 'right' },
  { key: 'live_check_in_avg_minutes', label: 'Avg Minutes', group: 'Live Check-ins', format: fmtNum, align: 'right' },
  { key: 'live_check_in_max_minutes', label: 'Max Minutes', group: 'Live Check-ins', format: fmtInt, align: 'right' },
  { key: 'last_live_check_in_at', label: 'Last Live', group: 'Live Check-ins', format: fmtDate, align: 'left' },

  { key: 'favorites_total', label: 'Favorites', group: 'Favorites', format: fmtInt, align: 'right' },

  { key: 'events_created', label: 'Events Created', group: 'Events', format: fmtInt, align: 'right' },
  { key: 'events_completed', label: 'Events Completed', group: 'Events', format: fmtInt, align: 'right' },
  { key: 'event_interests_going', label: 'Interests (Going)', group: 'Events', format: fmtInt, align: 'right' },
  { key: 'event_interests_interested', label: 'Interests', group: 'Events', format: fmtInt, align: 'right' },

  { key: 'deals_created', label: 'Deals Created', group: 'Deals', format: fmtInt, align: 'right' },
  { key: 'deal_views_total', label: 'Total Deal Views', group: 'Deals', format: fmtInt, align: 'right' },
  { key: 'deal_notifications_sent', label: 'Deals Sent', group: 'Deals', format: fmtInt, align: 'right' },
  { key: 'deal_notifications_tapped', label: 'Deals Opened', group: 'Deals', format: fmtInt, align: 'right' },

  { key: 'business_notifications_created', label: 'Notifications Sent', group: 'Biz Notifications', format: fmtInt, align: 'right' },
  { key: 'business_notification_deliveries_delivered', label: 'Delivered', group: 'Biz Notifications', format: fmtInt, align: 'right' },
  { key: 'business_notification_deliveries_opened', label: 'Opened', group: 'Biz Notifications', format: fmtInt, align: 'right' },
  { key: 'business_notification_max_subscriber_count', label: 'Total Subscribers', group: 'Biz Notifications', format: fmtInt, align: 'right' },
];

type SortDir = 'asc' | 'desc' | null;

function compareValues(a: Primitive, b: Primitive, dir: 'asc' | 'desc'): number {
  const nullishA = a === null || a === undefined;
  const nullishB = b === null || b === undefined;
  if (nullishA && nullishB) return 0;
  if (nullishA) return 1;
  if (nullishB) return -1;

  let cmp: number;
  if (typeof a === 'number' && typeof b === 'number') {
    cmp = a - b;
  } else if (typeof a === 'boolean' && typeof b === 'boolean') {
    cmp = a === b ? 0 : a ? 1 : -1;
  } else {
    cmp = String(a).localeCompare(String(b));
  }
  return dir === 'asc' ? cmp : -cmp;
}

interface GroupSpan {
  group: string;
  span: number;
  startIndex: number;
}

function computeGroupSpans(columns: Column[]): GroupSpan[] {
  const groups: GroupSpan[] = [];
  columns.forEach((col, idx) => {
    const last = groups[groups.length - 1];
    if (last && last.group === col.group) {
      last.span += 1;
    } else {
      groups.push({ group: col.group, span: 1, startIndex: idx });
    }
  });
  return groups;
}

function SortIndicator({ dir }: { dir: SortDir }): React.ReactElement {
  if (!dir) {
    return (
      <svg className="w-3 h-3 ml-1 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l4-4 4 4M8 15l4 4 4-4" />
      </svg>
    );
  }
  return (
    <svg className="w-3 h-3 ml-1 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
      {dir === 'asc' ? (
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      )}
    </svg>
  );
}

interface Props {
  rows: BusinessAnalyticsRow[];
  loading: boolean;
}

const GROUP_BG = 'bg-[#14142a]';
const HEADER_BG = 'bg-[#181831]';
const ROW_BG_EVEN = 'bg-[#1a1a2e]';
const ROW_BG_ODD = 'bg-[#161629]';

export default function AnalyticsTable({ rows, loading }: Props): React.ReactElement {
  const [sortKey, setSortKey] = useState<keyof BusinessAnalyticsRow | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);

  const groupSpans = useMemo(() => computeGroupSpans(COLUMNS), []);

  const sorted = useMemo(() => {
    if (!sortKey || !sortDir) return rows;
    const copy = [...rows];
    copy.sort((a, b) => compareValues(a[sortKey] as Primitive, b[sortKey] as Primitive, sortDir));
    return copy;
  }, [rows, sortKey, sortDir]);

  const onHeaderClick = (key: keyof BusinessAnalyticsRow) => {
    if (key !== sortKey) {
      setSortKey(key);
      setSortDir('desc');
      return;
    }
    if (sortDir === 'desc') {
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortKey(null);
      setSortDir(null);
    } else {
      setSortDir('desc');
    }
  };

  if (loading && rows.length === 0) {
    return (
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-2xl p-12 text-center">
        <div className="inline-flex items-center gap-3 text-gray-400 text-sm">
          <svg className="w-4 h-4 animate-spin text-violet-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          Loading business analytics…
        </div>
      </div>
    );
  }

  if (!loading && rows.length === 0) {
    return (
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-2xl p-12 text-center">
        <p className="text-gray-400 text-sm">No businesses matched your search.</p>
      </div>
    );
  }

  // First column sticky-left classes. Needs explicit bg per-cell because the
  // columns underneath scroll past it; transparent bg would show them through.
  const stickyCol = 'sticky left-0 z-10';

  return (
    <div className="bg-[#14142a] border border-gray-800/80 rounded-2xl shadow-xl shadow-black/20 overflow-hidden">
      <div className="overflow-auto max-h-[75vh] dark-scrollbar">
        <table className="min-w-full text-xs border-separate border-spacing-0">
          <thead>
            {/* Group row */}
            <tr>
              {groupSpans.map((g, gi) => {
                const isFirstGroup = gi === 0;
                const borderLeft = !isFirstGroup ? 'border-l border-gray-800/60' : '';
                return (
                  <th
                    key={g.group}
                    colSpan={g.span}
                    className={`${GROUP_BG} sticky top-0 z-20 px-3 py-2 text-center text-[10px] font-bold uppercase tracking-[0.15em] text-violet-300/70 border-b border-gray-800/80 ${borderLeft} ${
                      isFirstGroup ? `${stickyCol} z-30` : ''
                    }`}
                  >
                    {g.group}
                  </th>
                );
              })}
            </tr>
            {/* Column row */}
            <tr>
              {COLUMNS.map((col, ci) => {
                const isSorted = sortKey === col.key;
                const thisDir = isSorted ? sortDir : null;
                const isFirst = ci === 0;
                const isFirstOfGroup =
                  !isFirst && COLUMNS[ci - 1].group !== col.group;
                const leftBorder = isFirstOfGroup ? 'border-l border-gray-800/60' : '';
                const alignCls = col.align === 'right' ? 'justify-end text-right' : 'justify-start text-left';
                return (
                  <th
                    key={col.key as string}
                    onClick={() => onHeaderClick(col.key)}
                    className={`${HEADER_BG} sticky top-[30px] z-20 px-3 py-2.5 font-semibold text-[11px] uppercase tracking-wider whitespace-nowrap cursor-pointer select-none transition-colors border-b border-gray-800/80 group ${leftBorder} ${
                      isSorted ? 'text-violet-300' : 'text-gray-400 hover:text-gray-200'
                    } ${isFirst ? `${stickyCol} z-30` : ''}`}
                  >
                    <div className={`inline-flex items-center gap-0.5 ${alignCls} w-full`}>
                      <span>{col.label}</span>
                      <SortIndicator dir={thisDir} />
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => {
              const rowBg = i % 2 === 0 ? ROW_BG_EVEN : ROW_BG_ODD;
              return (
                <tr key={row.business_id} className="group">
                  {COLUMNS.map((col, ci) => {
                    const raw = row[col.key] as Primitive;
                    const out = col.format ? col.format(raw) : fmtStr(raw);
                    const isFirst = ci === 0;
                    const isFirstOfGroup =
                      !isFirst && COLUMNS[ci - 1].group !== col.group;
                    const leftBorder = isFirstOfGroup ? 'border-l border-gray-800/60' : '';
                    const dim = raw === 0 || raw === null || raw === undefined || raw === '';
                    const isName = col.key === 'business_name';
                    return (
                      <td
                        key={col.key as string}
                        className={`${rowBg} px-3 py-2.5 whitespace-nowrap border-b border-gray-800/40 transition-colors group-hover:bg-violet-950/20 ${leftBorder} ${
                          col.align === 'right' ? 'text-right tabular-nums' : 'text-left'
                        } ${
                          isName
                            ? 'font-semibold text-white'
                            : dim
                              ? 'text-gray-600'
                              : 'text-gray-200'
                        } ${isFirst ? `${stickyCol}` : ''}`}
                      >
                        {isName ? (
                          <Link
                            to={`/b2b/business-analytics/${row.business_id}`}
                            className="text-white hover:text-violet-300 transition-colors no-underline inline-flex items-center gap-1.5 group/link"
                          >
                            <span className="border-b border-transparent group-hover/link:border-violet-400/50">
                              {out}
                            </span>
                            <svg className="w-3 h-3 opacity-0 group-hover/link:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </Link>
                        ) : (
                          out
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 bg-[#14142a] border-t border-gray-800/60 text-[11px] text-gray-500 flex items-center justify-between">
        <span>{sorted.length} {sorted.length === 1 ? 'business' : 'businesses'}</span>
        <span className="text-gray-600">Click any column to sort · desc → asc → clear</span>
      </div>
    </div>
  );
}
